import { IColumn } from '@fluentui/react';
import { useCallback, useDebugValue, useMemo, useState } from 'react';

export type SortableType = string | number | boolean | null;
export type SortingProp<T> = keyof T | { key: string, selector(item: T): SortableType } | { key: string, comparer(item1: T, item2: T): number } | null;

interface SortInfo<T> {
    sortingProp: SortingProp<T>;
    isSortedDescending: boolean;
}

interface HookReturnType<T> {
    sortedItems: T[];
    sortingProp: SortingProp<T>;
    isSortedDescending: boolean;
    sortBy(sortingProp: SortingProp<T>, ascDesc?: 'ASC' | 'DESC'): void;
    isSortedBy(sortingProp: SortingProp<T>): boolean;
    columnSortProps(sortingProp: SortingProp<T>): {
        isSorted: IColumn['isSorted'];
        isSortedDescending: IColumn['isSortedDescending'];
        onColumnClick: IColumn['onColumnClick'];
    }
}

function useSortedListItems<T>(items: T[], defaultSortProp: SortingProp<T>): HookReturnType<T> {
    const [{ sortingProp, isSortedDescending }, setSortProps] = useState<SortInfo<T>>({ sortingProp: defaultSortProp, isSortedDescending: false });

    const sortedItems = useMemo<T[]>(
        () => {
            if (sortingProp === null)
                return items;

            let sorted: T[];
            if (typeof sortingProp === 'object') {
                if ('selector' in sortingProp)
                    sorted = items.sortBy(i => sortingProp.selector(i));
                else
                    sorted = [...items].sort(sortingProp.comparer);
            }
            else {
                sorted = items.sortBy(i => i[sortingProp] as unknown as SortableType);
            }

            if (isSortedDescending)
                sorted.reverse();

            return sorted;
        },
        [isSortedDescending, items, sortingProp],
    );

    const sortBy = useCallback(
        (prop: SortingProp<T>, ascDesc?: 'ASC' | 'DESC') => {
            setSortProps(curr => {
                if ((typeof prop === 'object' && typeof curr.sortingProp === 'object' && prop?.key === curr.sortingProp?.key) || prop === curr.sortingProp)
                    return { ...curr, isSortedDescending: ascDesc ? ascDesc === 'DESC' : !curr.isSortedDescending };
                else
                    return { sortingProp: prop, isSortedDescending: ascDesc ? ascDesc === 'DESC' : false };
            });
        },
        [],
    );

    const isSortedBy = useCallback(
        (prop: SortingProp<T>) => typeof sortingProp === 'object' && typeof prop === 'object'
            ? sortingProp?.key === prop?.key
            : sortingProp === prop,
        [sortingProp],
    );

    const columnSortProps = useCallback(
        (prop: SortingProp<T>) => ({
            isSorted: isSortedBy(prop),
            isSortedDescending,
            onColumnClick: () => sortBy(prop),
        }),
        [isSortedBy, isSortedDescending, sortBy],
    );

    useDebugValue({ by: typeof sortingProp === 'object' ? sortingProp?.key : sortingProp, descending: isSortedDescending });

    return {
        sortedItems,
        sortingProp,
        isSortedDescending,
        sortBy,
        isSortedBy,
        columnSortProps,
    };
}

export default useSortedListItems;